/**
 * Link targets → hrefs (spec 42 R3).
 *
 * Targets are typed objects, never raw hrefs into our own site, so a target
 * that has gone stale can be *dropped* instead of shipping a 404 at a buyer.
 * `resolveTarget` returns `null` for anything unrenderable — every caller must
 * treat null as "omit the control", never as "render it disabled".
 *
 * @see docs/spec/42-store-front-home-composer.md R3
 */
import type { LinkTarget } from "@flint/api-contract/store-home";

export interface TargetContext {
  storeSlug: string;
  /** Seller contact number; absent → the WhatsApp target cannot render. */
  phone: string | null;
  /**
   * Slugs that currently resolve. A collection that ended or was archived is
   * absent, which is what makes its CTA disappear rather than 404.
   */
  liveCollectionSlugs?: ReadonlySet<string>;
}

/**
 * WhatsApp deep link, spec 25 R3's rule verbatim: strip every non-digit, and
 * if exactly 10 digits remain prefix the country code. A number that is
 * already country-prefixed (or a landline with an STD code) is passed through
 * as-is — guessing at those does more harm than leaving them alone.
 */
export function whatsappHref(phone: string): string {
  const digits = phone.replace(/\D/g, "");
  const withCountry = digits.length === 10 ? `91${digits}` : digits;
  return `https://wa.me/${withCountry}`;
}

export function resolveTarget(target: LinkTarget, ctx: TargetContext): string | null {
  switch (target.kind) {
    case "collection":
      // Unknown-slug guard is opt-in: callers that did not pass the live set
      // are rendering data the server already filtered.
      if (ctx.liveCollectionSlugs && !ctx.liveCollectionSlugs.has(target.slug)) return null;
      return `/s/${ctx.storeSlug}/c/${target.slug}`;
    case "product":
      return `/s/${ctx.storeSlug}/p/${target.slug}`;
    case "page":
      // All published pages are readable; a link to an unpublished/removed page
      // 404s at the buyer, same tradeoff as a product slug.
      return `/s/${ctx.storeSlug}/pages/${target.slug}`;
    case "search":
      return target.q
        ? `/s/${ctx.storeSlug}/search?q=${encodeURIComponent(target.q)}`
        : `/s/${ctx.storeSlug}/search`;
    case "whatsapp":
      return ctx.phone ? whatsappHref(ctx.phone) : null;
    case "url":
      // The schema already refused anything but https:// at the write
      // boundary; this is the read-side backstop for rows written earlier.
      return target.href.startsWith("https://") ? target.href : null;
    default:
      return null;
  }
}

/** External links leave our origin, so they always get the opener guard. */
export function isExternal(target: LinkTarget): boolean {
  return target.kind === "url" || target.kind === "whatsapp";
}
